import axios from 'axios'
import logger from '@/utils/logger'

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:3000/api',
  timeout: 10000, // 10 seconds default timeout
  headers: {
    'Content-Type': 'application/json'
  }
})

// Store the auth0 instance that will be set by the plugin
let auth0Instance = null

// Function to set the auth0 instance from outside
export const setAuth0Instance = (instance) => {
  auth0Instance = instance
}

api.interceptors.request.use(
  async (config) => {
    const startTime = Date.now()
    config.metadata = { startTime }

    try {
      // Log API request start
      logger.debug('API Request Start', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        timeout: config.timeout,
        hasData: !!config.data
      })

      // Only try to get token if auth0Instance is available and user is authenticated
      if (auth0Instance && auth0Instance.isAuthenticated && auth0Instance.isAuthenticated.value) {
        const token = await auth0Instance.getAccessTokenSilently()
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          logger.debug('Added authorization token to request')
        }
      } else {
        logger.debug('No authentication token available or user not authenticated')
      }
    } catch (error) {
      logger.warn('Unable to get access token', error, {
        method: config.method,
        url: config.url
      })
      
      // If it's an invalid_grant error (refresh token expired/invalid), logout the user
      if (error?.error === 'invalid_grant' || error?.message?.includes('invalid_grant')) {
        logger.info('Refresh token expired, logging out user')
        if (auth0Instance && auth0Instance.logout) {
          auth0Instance.logout({ 
            logoutParams: { 
              returnTo: window.location.origin 
            } 
          })
        }
      }
    }
    
    return config
  },
  (error) => {
    logger.error('API Request Interceptor Error', error, {
      message: 'Error in request interceptor'
    })
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = response.config.metadata ? Date.now() - response.config.metadata.startTime : null

    // Log successful response
    logger.apiCall(
      response.config.method?.toUpperCase(),
      response.config.url,
      response.config.data,
      {
        status: response.status,
        statusText: response.statusText,
        dataSize: response.data ? JSON.stringify(response.data).length : 0,
        headers: Object.keys(response.headers || {})
      },
      duration
    )

    // Log performance warning for slow requests
    if (duration > 3000) {
      logger.warn('Slow API Response', {
        method: response.config.method,
        url: response.config.url,
        duration,
        status: response.status
      })
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config
    const duration = originalRequest.metadata ? Date.now() - originalRequest.metadata.startTime : null

    // Log API error
    logger.apiCall(
      originalRequest.method?.toUpperCase(),
      originalRequest.url,
      originalRequest.data,
      error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: Object.keys(error.response.headers || {})
      } : null,
      duration
    )
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      logger.info('Attempting token refresh due to 401 error', {
        url: originalRequest.url,
        method: originalRequest.method
      })
      
      try {
        // Only try to refresh token if auth0Instance is available
        if (auth0Instance && auth0Instance.isAuthenticated && auth0Instance.isAuthenticated.value) {
          const token = await auth0Instance.getAccessTokenSilently({
            cacheMode: 'off'
          })
          
          if (token) {
            logger.debug('Token refresh successful, retrying request')
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          }
        } else {
          // If not authenticated, redirect to login
          logger.info('User not authenticated, redirecting to login')
          window.location.href = '/login'
        }
      } catch (refreshError) {
        logger.error('Token refresh failed', refreshError, {
          originalUrl: originalRequest.url,
          originalMethod: originalRequest.method
        })
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Log specific error types
    if (error.code === 'ECONNABORTED') {
      logger.error('API Timeout Error', error, {
        url: originalRequest.url,
        method: originalRequest.method,
        timeout: originalRequest.timeout
      })
    } else if (!error.response) {
      logger.error('Network Error', error, {
        url: originalRequest.url,
        method: originalRequest.method,
        message: 'No response received from server'
      })
    }
    
    return Promise.reject(error)
  }
)

// API methods for bookings
export const bookingAPI = {
  async getBooking(bookingId) {
    logger.debug('Fetching booking details', { bookingId })
    try {
      const response = await api.get(`/booking/${bookingId}`)
      logger.info('Successfully fetched booking', { 
        bookingId, 
        bookingReference: response.data?.booking_reference 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch booking', error, { bookingId })
      throw error
    }
  },

  async createCashBooking(payload) {
    logger.info('Creating cash booking', {
      bookingReference: payload.bookingReference,
      rebookedFrom: payload.rebookedFrom?.bookingReference
    })
    try {
      const response = await api.post('/cash-booking', payload, { timeout: 30000 })
      logger.info('Successfully created cash booking', {
        bookingReference: response.data?.booking_reference
      })
      return response
    } catch (error) {
      logger.error('Failed to create cash booking', error, { bookingReference: payload.bookingReference })
      throw error
    }
  },

  async getAllBookings(params = {}) {
    logger.debug('Fetching all bookings', { params })
    try {
      const response = await api.get('/bookings', { params })
      logger.info('Successfully fetched bookings', { 
        count: response.data?.bookings?.length || 0,
        params 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch bookings', error, { params })
      throw error
    }
  },

  async cancelBooking({ formBookingId, reason, notes, actorEmail }) {
    logger.debug('Cancelling booking', { formBookingId, reason })
    try {
      const response = await api.post('/bookings/cancel', {
        formBookingId,
        reason,
        notes,
        actorEmail
      })
      logger.info('Booking cancelled', {
        formBookingId,
        releasedCount: response.data?.releasedCount || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to cancel booking', error, { formBookingId })
      throw error
    }
  },

  async updateBookingStatus(bookingId, status, notes = '') {
    logger.info('Updating booking status', { bookingId, status, hasNotes: !!notes })
    try {
      const response = await api.post('/bookings/update-job-status', {
        booking_id: bookingId,
        status,
        notes
      })
      logger.info('Successfully updated booking status', { bookingId, status })
      return response
    } catch (error) {
      logger.error('Failed to update booking status', error, { bookingId, status })
      throw error
    }
  },

  async updateBooking(formBookingId, updates) {
    logger.info('Updating booking', { formBookingId, updates })
    try {
      const response = await api.post('/bookings/update-booking', {
        form_booking_id: formBookingId,
        updates
      })
      logger.info('Successfully updated booking', { formBookingId })
      return response
    } catch (error) {
      logger.error('Failed to update booking', error, { formBookingId })
      throw error
    }
  },

  async getBookingPayments(bookingReference, formBookingId = null) {
    logger.debug('Fetching booking payment history', { bookingReference, formBookingId })
    try {
      const params = new URLSearchParams()
      if (formBookingId) {
        params.append('formBookingId', formBookingId)
      }

      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await api.get(`/bookings/${encodeURIComponent(bookingReference)}/payments${queryString}`)

      logger.info('Successfully fetched booking payments', {
        bookingReference,
        paymentCount: response.data?.payments?.length || 0,
        totalAmount: response.data?.summary?.total_attempted_amount || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch booking payments', error, { bookingReference, formBookingId })
      throw error
    }
  },

  async updateJobStatus(bookingReference, formBookingId, status, notes = '', technicianId) {
    logger.info('Updating job status', {
      bookingReference,
      formBookingId,
      status,
      hasNotes: !!notes,
      technicianId
    })
    try {
      const payload = {
        booking_reference: bookingReference,
        status: status,
        technician_notes: notes,
        technician_id: technicianId,
        is_admin: true
      }

      // Only include form_booking_id if provided
      if (formBookingId) {
        payload.form_booking_id = formBookingId
      }

      logger.debug('Job status update payload', payload)

      const response = await api.post('/bookings/update-job-status', payload)
      logger.info('Successfully updated job status', {
        bookingReference,
        newStatus: status
      })
      return response
    } catch (error) {
      logger.error('Failed to update job status', error, {
        bookingReference,
        status
      })
      throw error
    }
  },

  async getJobStatus(bookingReference, formBookingId = null) {
    logger.debug('Fetching job status history', { bookingReference, formBookingId })
    try {
      const params = new URLSearchParams()

      if (bookingReference) {
        params.append('booking_reference', bookingReference)
      }

      if (formBookingId) {
        params.append('form_booking_id', formBookingId)
      }

      const response = await api.get(`/bookings/get-job-status?${params.toString()}`)
      logger.info('Successfully fetched job status history', {
        bookingReference,
        historyCount: response.data?.status_history?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch job status history', error, {
        bookingReference,
        formBookingId
      })
      throw error
    }
  },

  async getBookingSwaps(formBookingId) {
    logger.debug('Fetching booking swap history', { formBookingId })
    try {
      const response = await api.get(`/bookings/swaps`, {
        params: { formBookingId }
      })
      logger.info('Successfully fetched booking swaps', {
        formBookingId,
        count: response.data?.swaps?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch booking swaps', error, { formBookingId })
      throw error
    }
  },

  async getBookingReschedules(formBookingId) {
    logger.debug('Fetching booking reschedule history', { formBookingId })
    try {
      const response = await api.get(`/bookings/reschedules`, {
        params: { formBookingId }
      })
      logger.info('Successfully fetched booking reschedules', {
        formBookingId,
        count: response.data?.reschedules?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch booking reschedules', error, { formBookingId })
      throw error
    }
  },

  async getBookingReassignments(formBookingId) {
    logger.debug('Fetching booking reassignment history', { formBookingId })
    try {
      const response = await api.get(`/bookings/reassignments`, {
        params: { formBookingId }
      })
      logger.info('Successfully fetched booking reassignments', {
        formBookingId,
        count: response.data?.reassignments?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch booking reassignments', error, { formBookingId })
      throw error
    }
  }
}

// Admin slot blocking APIs
export const slotAdminAPI = {
  async getVehicleStatus(startDate, endDate) {
    return api.get('/booking-slots/vehicle-status', { params: { startDate, endDate } })
  },
  async blockVehicleDay({ serviceDate, vehicleId, reason, createdBy }) {
    return api.post('/booking-slots/block-vehicle-day', { serviceDate, vehicleId, reason, createdBy })
  },
  async unblockVehicleDay({ serviceDate, vehicleId, reason, releasedBy }) {
    return api.post('/booking-slots/unblock-vehicle-day', { serviceDate, vehicleId, reason, releasedBy })
  },
  async blockSlot({ serviceDate, timeSlotId, vehicleId, reason, createdBy }) {
    return api.post('/booking-slots/block-slot', { serviceDate, timeSlotId, vehicleId, reason, createdBy })
  },
  async unblockSlot({ serviceDate, timeSlotId, vehicleId, reason, releasedBy }) {
    return api.post('/booking-slots/unblock-slot', { serviceDate, timeSlotId, vehicleId, reason, releasedBy })
  }
}

// API methods for technicians
export const technicianAPI = {
  async getAllTechnicians() {
    logger.debug('Fetching all technicians')
    try {
      const response = await api.get('/technicians')
      logger.info('Successfully fetched technicians', { 
        count: response.data?.technicians?.length || 0 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch technicians', error)
      throw error
    }
  },

  async getTechnicianProfile(technicianId) {
    logger.debug('Fetching technician profile', { technicianId })
    try {
      const response = await api.get('/technician-profile', {
        params: { technician_id: technicianId }
      })
      logger.info('Successfully fetched technician profile', { technicianId })
      return response
    } catch (error) {
      logger.error('Failed to fetch technician profile', error, { technicianId })
      throw error
    }
  },

  async createTechnician(technicianData) {
    logger.info('Creating new technician', { 
      name: `${technicianData.first_name} ${technicianData.last_name}`,
      role: technicianData.role_title 
    })
    try {
      const response = await api.post('/create-technician', technicianData)
      logger.info('Successfully created technician', { 
        technicianId: response.data?.technician_id 
      })
      return response
    } catch (error) {
      logger.error('Failed to create technician', error, { 
        name: `${technicianData.first_name} ${technicianData.last_name}` 
      })
      throw error
    }
  }
}

// API methods for customers
export const customerAPI = {
  async getAllCustomers(params = {}) {
    logger.debug('Fetching all customers', { params })
    try {
      const response = await api.get('/customers', { params })
      logger.info('Successfully fetched customers', { 
        count: response.data?.customers?.length || 0,
        params 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch customers', error, { params })
      throw error
    }
  },

  async getCustomer(customerId) {
    logger.debug('Fetching customer profile', { customerId })
    try {
      const response = await api.get(`/customer/${customerId}`)
      logger.info('Successfully fetched customer', { 
        customerId,
        customerName: `${response.data?.first_name} ${response.data?.last_name}`
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch customer', error, { customerId })
      throw error
    }
  },

  async createCustomer(customerData) {
    logger.info('Creating new customer', { 
      name: `${customerData.first_name} ${customerData.last_name}`,
      email: customerData.email 
    })
    try {
      const response = await api.post('/create-customer', customerData)
      logger.info('Successfully created customer', { 
        customerId: response.data?.customer_id 
      })
      return response
    } catch (error) {
      logger.error('Failed to create customer', error, { 
        email: customerData.email 
      })
      throw error
    }
  }
}

// API methods for payments
export const paymentsAPI = {
  async getAllPayments(params = {}) {
    logger.debug('Fetching all payments', { params })
    try {
      const response = await api.get('/payments', { params })
      logger.info('Successfully fetched payments', { 
        count: response.data?.payments?.length || 0,
        params 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch payments', error, { params })
      throw error
    }
  },

  async getPaymentDetails(paymentId) {
    logger.debug('Fetching payment details', { paymentId })
    try {
      const response = await api.get(`/payments/${paymentId}`)
      logger.info('Successfully fetched payment details', { paymentId })
      return response
    } catch (error) {
      logger.error('Failed to fetch payment details', error, { paymentId })
      throw error
    }
  },

  async getBookingPayments(bookingReference) {
    logger.debug('Fetching booking payments', { bookingReference })
    try {
      const response = await api.get(`/bookings/${bookingReference}/payments`)
      logger.info('Successfully fetched booking payments', {
        bookingReference,
        count: response.data?.payments?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch booking payments', error, { bookingReference })
      throw error
    }
  },

  async generatePaymentLink(bookingData, customAmount = null, customDescription = null) {
    logger.info('Generating payment link', { 
      bookingReference: bookingData.booking_reference, 
      hasCustomAmount: customAmount !== null,
      hasCustomDescription: customDescription !== null 
    })
    try {
      // Prepare comprehensive request data with all booking information
      const requestData = {
        // Required fields
        booking_reference: bookingData.booking_reference,
        form_booking_id: bookingData.form_booking_id || bookingData.id, // Use DB id if form_booking_id not available
        customer_id: bookingData.customer_id,
        first_name: bookingData.first_name,
        last_name: bookingData.last_name,
        email: bookingData.email,
        // Use final_amount_paid (discounted) if available, otherwise total_amount
        total_amount: bookingData.final_amount_paid || bookingData.total_amount,
        original_amount: bookingData.total_amount,
        discount_code: bookingData.discount_code || '',
        discount_amount: bookingData.discount_amount || 0,
        
        // Optional customer information
        phone: bookingData.phone || '',
        address: bookingData.address || '',
        company_name: bookingData.company_name || '',
        
        // Service information
        service_type: bookingData.service_type || '',
        pest_category: bookingData.pest_category || '',
        scheduled_date: bookingData.scheduled_date || '',
        scheduled_time: bookingData.scheduled_time || '',
        status: bookingData.status || '',
        
        // Property details
        house_size: bookingData.house_size || '',
        house_levels: bookingData.house_levels || '',
        commercial_size: bookingData.commercial_size || '',
        commercial_type: bookingData.commercial_type || '',
        
        // Additional booking details
        selected_pests: bookingData.selected_pests || [],
        pest_areas: bookingData.pest_areas || [],
        customer_notes: bookingData.customer_notes || '',
        service_breakdown: bookingData.service_breakdown || {},
        
        // Additional fields for metadata
        wasp_nest_count: bookingData.wasp_nest_count || '',
        wasp_nest_locations: bookingData.wasp_nest_locations || '',
        wasp_nest_size: bookingData.wasp_nest_size || '',
        treatment_areas: bookingData.treatment_areas || [],
        special_instructions: bookingData.special_instructions || '',
        access_notes: bookingData.access_notes || '',
        property_type: bookingData.property_type || '',
        building_type: bookingData.building_type || '',
        created_at: bookingData.created_at || '',
        
        // Admin payment flags
        admin_payment: true,
        
        // Custom payment options
        ...(customAmount !== null && { amount: customAmount }),
        ...(customDescription !== null && { description: customDescription })
      }
      
      logger.debug('Payment link request data', { 
        bookingReference: requestData.booking_reference,
        customerId: requestData.customer_id,
        formBookingId: requestData.form_booking_id,
        customerName: `${requestData.first_name} ${requestData.last_name}`,
        amount: requestData.amount || requestData.total_amount
      })
      
      const response = await api.post('/payments/generate-link', requestData)
      logger.info('Successfully generated payment link', { 
        bookingReference: requestData.booking_reference,
        paymentUrl: response.data?.payment_url,
        amount: response.data?.amount
      })
      return response
    } catch (error) {
      logger.error('Failed to generate payment link', error, { 
        bookingReference: bookingData.booking_reference,
        customAmount,
        customDescription 
      })
      throw error
    }
  },

  async processTerminalPayment(bookingReference, formBookingId, paymentMethodId, amount, metadata = {}) {
    logger.info('Processing terminal payment (MOTO)', {
      bookingReference,
      formBookingId,
      amount,
      paymentMethodId: paymentMethodId.substring(0, 10) + '...'
    })
    try {
      const requestData = {
        bookingReference,
        formBookingId,
        paymentMethodId,
        amount,
        metadata
      }

      logger.debug('Terminal payment request data', {
        bookingReference,
        amount,
        hasMetadata: Object.keys(metadata).length > 0
      })

      const response = await api.post('/payments/terminal', requestData)
      logger.info('Successfully processed terminal payment', {
        bookingReference,
        paymentIntentId: response.data?.payment_intent_id,
        amount: response.data?.amount,
        status: response.data?.status
      })
      return response
    } catch (error) {
      logger.error('Failed to process terminal payment', error, {
        bookingReference,
        formBookingId,
        amount
      })
      throw error
    }
  },

  async sendReceipt(bookingData) {
    logger.info('Sending receipt email', {
      bookingReference: bookingData.booking_reference,
      customerEmail: bookingData.email
    })
    try {
      const serviceItems = bookingData.service_breakdown || bookingData.serviceBreakdown || []
      const requestData = {
        booking_reference: bookingData.booking_reference,
        form_booking_id: bookingData.form_booking_id || bookingData.id,
        customer_id: bookingData.customer_id,
        customer_name: `${bookingData.first_name} ${bookingData.last_name}`,
        customer_email: bookingData.email,
        customer_phone: bookingData.phone || '',
        customer_address: bookingData.address || '',
        service_type: bookingData.service_type || '',
        service_date: bookingData.scheduled_date || '',
        total_amount: bookingData.total_amount,
        payment_method: bookingData.payment_method || 'Card',
        service_items: serviceItems.map(item => ({
          name: item.name || item.label || item.description,
          amount: item.price || item.value || item.amount
        }))
      }

      logger.debug('Receipt request data', {
        bookingReference: requestData.booking_reference,
        customerName: requestData.customer_name,
        amount: requestData.total_amount
      })

      const response = await api.post('/receipts/send', requestData)
      logger.info('Successfully sent receipt', {
        bookingReference: requestData.booking_reference,
        receiptNumber: response.data?.receipt_number,
        emailSent: response.data?.email_sent
      })
      return response
    } catch (error) {
      logger.error('Failed to send receipt', error, {
        bookingReference: bookingData.booking_reference,
        customerEmail: bookingData.email
      })
      throw error
    }
  },

  async previewReceipt(bookingData) {
    logger.info('Previewing receipt PDF', {
      bookingReference: bookingData.booking_reference
    })
    try {
      const serviceItems = bookingData.service_breakdown || bookingData.serviceBreakdown || []
      const requestData = {
        preview_only: true,
        booking_reference: bookingData.booking_reference,
        form_booking_id: bookingData.form_booking_id || bookingData.id,
        customer_id: bookingData.customer_id,
        customer_name: `${bookingData.first_name} ${bookingData.last_name}`,
        customer_email: bookingData.email || '',
        customer_address: bookingData.address || '',
        service_type: bookingData.service_type || '',
        service_date: bookingData.scheduled_date || '',
        total_amount: bookingData.total_amount,
        payment_method: bookingData.payment_method || 'Card',
        service_items: serviceItems.map(item => ({
          name: item.name || item.label || item.description,
          amount: item.price || item.value || item.amount
        }))
      }

      logger.debug('Receipt preview request data', {
        bookingReference: requestData.booking_reference,
        customerName: requestData.customer_name
      })

      const response = await api.post('/receipts/send', requestData, {
        timeout: 30000 // 30 seconds for PDF generation
      })
      logger.info('Successfully generated receipt preview', {
        bookingReference: requestData.booking_reference,
        receiptNumber: response.data?.receipt_number
      })
      return response
    } catch (error) {
      logger.error('Failed to preview receipt', error, {
        bookingReference: bookingData.booking_reference
      })
      throw error
    }
  },

  async sendInvoice(bookingData) {
    logger.info('Sending invoice email', {
      bookingReference: bookingData.booking_reference,
      customerEmail: bookingData.email
    })
    try {
      const serviceItems = bookingData.service_breakdown || bookingData.serviceBreakdown || []
      const requestData = {
        booking_reference: bookingData.booking_reference,
        form_booking_id: bookingData.form_booking_id || bookingData.id,
        customer_id: bookingData.customer_id,
        customer_name: bookingData.invoice_recipient_name?.trim() || [bookingData.first_name, bookingData.last_name].filter(Boolean).join(' '),
        customer_email: bookingData.email,
        customer_phone: bookingData.phone || '',
        customer_address: bookingData.address || '',
        service_type: bookingData.service_type || '',
        service_date: bookingData.scheduled_date || '',
        total_amount: bookingData.total_amount,
        service_items: serviceItems.map(item => ({
          name: item.name || item.label || item.description,
          amount: item.price || item.value || item.amount
        }))
      }

      logger.debug('Invoice request data', {
        bookingReference: requestData.booking_reference,
        customerName: requestData.customer_name,
        amount: requestData.total_amount
      })

      const response = await api.post('/invoices/send', requestData)
      logger.info('Successfully sent invoice', {
        bookingReference: requestData.booking_reference,
        invoiceNumber: response.data?.invoice_number,
        emailSent: response.data?.email_sent
      })
      return response
    } catch (error) {
      logger.error('Failed to send invoice', error, {
        bookingReference: bookingData.booking_reference,
        customerEmail: bookingData.email
      })
      throw error
    }
  },

  async previewInvoice(bookingData) {
    logger.info('Previewing invoice PDF', {
      bookingReference: bookingData.booking_reference
    })
    try {
      const serviceItems = bookingData.service_breakdown || bookingData.serviceBreakdown || []
      const requestData = {
        preview_only: true,
        booking_reference: bookingData.booking_reference,
        form_booking_id: bookingData.form_booking_id || bookingData.id,
        customer_id: bookingData.customer_id,
        customer_name: bookingData.invoice_recipient_name?.trim() || [bookingData.first_name, bookingData.last_name].filter(Boolean).join(' '),
        customer_email: bookingData.email || '',
        customer_address: bookingData.address || '',
        service_type: bookingData.service_type || '',
        service_date: bookingData.scheduled_date || '',
        total_amount: bookingData.total_amount,
        service_items: serviceItems.map(item => ({
          name: item.name || item.label || item.description,
          amount: item.price || item.value || item.amount
        }))
      }

      logger.debug('Invoice preview request data', {
        bookingReference: requestData.booking_reference,
        customerName: requestData.customer_name
      })

      const response = await api.post('/invoices/send', requestData, {
        timeout: 30000 // 30 seconds for PDF generation
      })
      logger.info('Successfully generated invoice preview', {
        bookingReference: requestData.booking_reference,
        invoiceNumber: response.data?.invoice_number
      })
      return response
    } catch (error) {
      logger.error('Failed to preview invoice', error, {
        bookingReference: bookingData.booking_reference
      })
      throw error
    }
  },

  async listInvoices({ limit = 20, search = '', startDate = null, endDate = null, nextToken = null } = {}) {
    const params = new URLSearchParams()
    params.append('limit', String(limit))
    if (search) params.append('search', search)
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    if (nextToken) params.append('nextToken', nextToken)

    try {
      const response = await api.get(`/invoices/list?${params.toString()}`)
      return response.data
    } catch (error) {
      logger.error('Failed to list invoices', error, { search, startDate, endDate })
      throw error
    }
  },

  async getInvoicePresignedUrl(s3Key) {
    try {
      const response = await api.post('/invoices/presigned-url', { s3Key })
      return response.data?.url
    } catch (error) {
      logger.error('Failed to get invoice presigned URL', error, { s3Key })
      throw error
    }
  },

  async listReceipts({ limit = 20, search = '', startDate = null, endDate = null, nextToken = null } = {}) {
    const params = new URLSearchParams()
    params.append('limit', String(limit))
    if (search) params.append('search', search)
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    if (nextToken) params.append('nextToken', nextToken)

    try {
      const response = await api.get(`/receipts/list?${params.toString()}`)
      return response.data
    } catch (error) {
      logger.error('Failed to list receipts', error, { search, startDate, endDate })
      throw error
    }
  },

  async getReceiptPresignedUrl(s3Key) {
    try {
      const response = await api.post('/receipts/presigned-url', { s3Key })
      return response.data?.url
    } catch (error) {
      logger.error('Failed to get receipt presigned URL', error, { s3Key })
      throw error
    }
  },

  async listQuotes({ limit = 20, search = '', startDate = null, endDate = null, nextToken = null } = {}) {
    const params = new URLSearchParams()
    params.append('limit', String(limit))
    if (search) params.append('search', search)
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    if (nextToken) params.append('nextToken', nextToken)

    try {
      const response = await api.get(`/quotes/list?${params.toString()}`)
      return response.data
    } catch (error) {
      logger.error('Failed to list quotes', error, { search, startDate, endDate })
      throw error
    }
  },

  async getQuotePresignedUrl(s3Key) {
    try {
      const response = await api.post('/quotes/presigned-url', { s3Key })
      return response.data?.url
    } catch (error) {
      logger.error('Failed to get quote presigned URL', error, { s3Key })
      throw error
    }
  }
}

// API methods for vehicles
export const vehicleAPI = {
  async getAllVehicles() {
    logger.debug('Fetching all vehicles')
    try {
      const response = await api.get('/vehicles')
      logger.info('Successfully fetched vehicles', {
        count: response.data?.data?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch vehicles', error)
      throw error
    }
  },

  async assignTechnician(vehicleId, technicianId) {
    logger.info('Assigning technician to vehicle', { vehicleId, technicianId })
    try {
      const response = await api.post(`/vehicle/${vehicleId}/assign`, {
        technician_id: technicianId
      })
      logger.info('Successfully assigned technician to vehicle', { vehicleId, technicianId })
      return response
    } catch (error) {
      logger.error('Failed to assign technician to vehicle', error, { vehicleId, technicianId })
      throw error
    }
  },

  async unassignTechnician(vehicleId, technicianId) {
    logger.info('Unassigning technician from vehicle', { vehicleId, technicianId })
    try {
      const response = await api.post(`/vehicle/${vehicleId}/unassign`, {
        technician_id: technicianId
      })
      logger.info('Successfully unassigned technician from vehicle', { vehicleId, technicianId })
      return response
    } catch (error) {
      logger.error('Failed to unassign technician from vehicle', error, { vehicleId, technicianId })
      throw error
    }
  }
}

// API methods for booking slots insights
export const slotsInsightsAPI = {
  async getOverview(filters = {}) {
    logger.debug('Fetching booking slots overview', { filters })
    try {
      const response = await api.get('/admin/booking-slots/overview', { params: filters })
      logger.info('Successfully fetched slots overview', { 
        slotsCount: response.data?.data?.slots?.length || 0,
        filters 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch slots overview', error, { filters })
      throw error
    }
  },
  
  async getActiveLocks() {
    logger.debug('Fetching active booking locks')
    try {
      const response = await api.get('/admin/booking-slots/locks')
      logger.info('Successfully fetched active locks', { 
        activeLocksCount: response.data?.data?.activeLocks?.length || 0 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch active locks', error)
      throw error
    }
  },

  async getDropoffs(filters = {}) {
    logger.debug('Fetching non-converted locks (dropoffs)', { filters })
    try {
      const response = await api.get('/admin/booking-slots/dropoffs', { params: filters })
      logger.info('Successfully fetched dropoffs data', { 
        dropoffsCount: response.data?.data?.nonConvertedLocks?.length || 0,
        period: response.data?.data?.period,
        filters 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch dropoffs data', error, { filters })
      throw error
    }
  },
  
  async getCapacityData(dateRange = {}) {
    logger.debug('Fetching capacity insights', { dateRange })
    try {
      const response = await api.get('/admin/booking-slots/capacity', { params: dateRange })
      logger.info('Successfully fetched capacity data', { 
        capacityEntriesCount: response.data?.data?.capacityDetails?.length || 0,
        dateRange 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch capacity data', error, { dateRange })
      throw error
    }
  },
  
  async getVehicleInsights() {
    logger.debug('Fetching vehicle insights')
    try {
      const response = await api.get('/admin/booking-slots/vehicles')
      logger.info('Successfully fetched vehicle insights', { 
        vehiclesCount: response.data?.data?.vehicles?.length || 0 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch vehicle insights', error)
      throw error
    }
  },
  
  async getLockAnalytics(period = '7d') {
    logger.debug('Fetching lock analytics', { period })
    try {
      const response = await api.get('/admin/booking-slots/analytics', { params: { period }})
      logger.info('Successfully fetched lock analytics', { 
        period,
        dailyEntriesCount: response.data?.data?.dailyAnalytics?.length || 0 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch lock analytics', error, { period })
      throw error
    }
  }
}

// API methods for slot management
export const slotsAPI = {
  async getAvailableSlots(startDate, endDate, serviceType = null) {
    logger.debug('Fetching available slots', { startDate, endDate, serviceType })
    try {
      const params = new URLSearchParams()
      params.append('startDate', startDate)
      params.append('endDate', endDate)
      if (serviceType) {
        params.append('serviceType', serviceType)
      }

      const response = await api.get(`/booking-slots/available-slots?${params.toString()}`)
      logger.info('Successfully fetched available slots', { 
        startDate, 
        endDate,
        slotCount: response.data?.length || 0 
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch available slots', error, { startDate, endDate })
      throw error
    }
  },

  async lockSlot({ serviceDate, timeSlotId, sessionId, customerEmail, serviceType }) {
    logger.info('Locking slot', { serviceDate, timeSlotId, serviceType })
    try {
      return await api.post('/booking-slots/lock-slot', {
        serviceDate,
        timeSlotId,
        sessionId,
        customerEmail,
        serviceType,
        lockDurationMinutes: 15
      })
    } catch (error) {
      logger.error('Failed to lock slot', error, { serviceDate, timeSlotId })
      throw error
    }
  },

  async releaseLock({ slotId, sessionId }) {
    try {
      return await api.post('/booking-slots/release-lock', { slotId, sessionId })
    } catch (error) {
      logger.error('Failed to release slot lock', error, { slotId })
      throw error
    }
  },



  async rescheduleBooking(rescheduleData) {
    logger.info('Rescheduling booking', {
      bookingId: rescheduleData.bookingId,
      newDate: rescheduleData.newDate,
      reason: rescheduleData.reason 
    })
    try {
      const response = await api.post('/booking-slots/reschedule-booking', rescheduleData)
      logger.info('Successfully rescheduled booking', { 
        bookingId: rescheduleData.bookingId,
        newSlotId: response.data?.newSlotId 
      })
      return response
    } catch (error) {
      logger.error('Failed to reschedule booking', error, { bookingId: rescheduleData.bookingId })
      throw error
    }
  },



  async reassignVehicle(reassignData) {
    logger.info('Reassigning vehicle for booking', {
      formBookingId: reassignData.formBookingId,
      newVehicleId: reassignData.newVehicleId,
      reason: reassignData.reason
    })
    try {
      const response = await api.post('/booking-slots/reassign-vehicle', reassignData)
      logger.info('Successfully reassigned vehicle', {
        formBookingId: reassignData.formBookingId,
        newVehicleId: response.data?.newVehicleId,
        newVehicleName: response.data?.newVehicleName
      })
      return response
    } catch (error) {
      logger.error('Failed to reassign vehicle', error, {
        formBookingId: reassignData.formBookingId,
        newVehicleId: reassignData.newVehicleId
      })
      throw error
    }
  },

  async swapBookings(formBookingIdA, formBookingIdB, reason, notes, swappedBy) {
    logger.info('Swapping bookings', {
      formBookingIdA,
      formBookingIdB,
      reason,
      swappedBy
    })
    try {
      const response = await api.post('/booking-slots/swap-slots', {
        formBookingIdA,
        formBookingIdB,
        reason,
        notes,
        swappedBy
      })
      logger.info('Successfully swapped bookings', {
        formBookingIdA,
        formBookingIdB,
        bookingA: response.data?.bookingA,
        bookingB: response.data?.bookingB
      })
      return response
    } catch (error) {
      logger.error('Failed to swap bookings', error, {
        formBookingIdA,
        formBookingIdB,
        reason
      })
      throw error
    }
  },

  async getBookingSwapCandidates(formBookingId) {
    logger.info('Fetching swap candidates for booking', { formBookingId })
    try {
      const params = new URLSearchParams()
      params.append('formBookingId', formBookingId)

      const response = await api.get(`/booking-slots/swap-candidates?${params.toString()}`)
      logger.info('Successfully fetched swap candidates', {
        formBookingId,
        count: response.data?.count || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch swap candidates', error, {
        formBookingId
      })
      throw error
    }
  },

  async getVehicleSlots(serviceDate, vehicleId) {
    logger.debug('Fetching vehicle-specific slots', { serviceDate, vehicleId })
    try {
      const params = new URLSearchParams()
      params.append('serviceDate', serviceDate)
      params.append('vehicleId', vehicleId)

      const response = await api.get(`/booking-slots/vehicle-slots?${params.toString()}`)
      logger.info('Successfully fetched vehicle slots', {
        serviceDate,
        vehicleId,
        slotsCount: response.data?.slots?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch vehicle slots', error, { serviceDate, vehicleId })
      throw error
    }
  }
}

// API methods for leads management
export const leadsAPI = {
  async getUnconvertedLeads(params = {}) {
    logger.debug('Fetching unconverted leads', { params })
    try {
      const response = await api.get('/marketing/leads', { params })
      logger.info('Successfully fetched unconverted leads', {
        count: response.data?.data?.length || 0,
        params
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch unconverted leads', error, { params })
      throw error
    }
  },

  async getNotes(leadId) {
    logger.debug('Fetching lead notes', { leadId })
    try {
      const response = await api.get(`/marketing/leads/${leadId}/notes`)
      logger.info('Successfully fetched lead notes', {
        leadId,
        count: response.data?.data?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch lead notes', error, { leadId })
      throw error
    }
  },

  async addNote(leadId, noteData) {
    logger.info('Adding note to lead', { leadId, hasNote: !!noteData.note })
    try {
      const response = await api.post(`/marketing/leads/${leadId}/notes`, noteData)
      logger.info('Successfully added note to lead', {
        leadId,
        noteId: response.data?.data?.id
      })
      return response
    } catch (error) {
      logger.error('Failed to add note to lead', error, { leadId })
      throw error
    }
  }
}

// API methods for SMS conversations
export const conversationAPI = {
  async getConversations(params = {}) {
    logger.debug('Fetching SMS conversations', { params })
    try {
      const response = await api.get('/sms/conversations', { params })
      logger.info('Successfully fetched conversations', {
        count: response.data?.data?.length || 0,
        unreadCount: response.data?.unread_count || 0,
        params
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch conversations', error, { params })
      throw error
    }
  },

  async getMessages(conversationId, params = {}) {
    logger.debug('Fetching conversation messages', { conversationId, params })
    try {
      const response = await api.get(`/sms/conversations/${conversationId}/messages`, { params })
      logger.info('Successfully fetched messages', {
        conversationId,
        count: response.data?.messages?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch messages', error, { conversationId, params })
      throw error
    }
  },

  async sendMessage(conversationId, message, sentBy) {
    logger.info('Sending SMS reply', { conversationId, messageLength: message?.length })
    try {
      const response = await api.post(`/sms/conversations/${conversationId}/reply`, { message, sent_by: sentBy })
      logger.info('Successfully sent message', {
        conversationId,
        messageId: response.data?.message?.id
      })
      return response
    } catch (error) {
      logger.error('Failed to send message', error, { conversationId })
      throw error
    }
  },

  async markAsRead(conversationId) {
    logger.debug('Marking conversation as read', { conversationId })
    try {
      // The messages endpoint auto-marks as read
      const response = await api.get(`/sms/conversations/${conversationId}/messages`, {
        params: { limit: 1, mark_read: true }
      })
      logger.info('Marked conversation as read', { conversationId })
      return response
    } catch (error) {
      logger.error('Failed to mark as read', error, { conversationId })
      throw error
    }
  }
}

// API methods for Voice Calls
export const voiceAPI = {
  async getToken() {
    logger.debug('Fetching voice access token')
    try {
      const response = await api.get('/voice/token')
      logger.info('Successfully fetched voice token', {
        identity: response.data?.identity,
        ttl: response.data?.ttl
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch voice token', error)
      throw error
    }
  },

  async getCallLogs(conversationId, params = {}) {
    logger.debug('Fetching call logs', { conversationId, params })
    try {
      const response = await api.get(`/voice/calls/${conversationId}`, { params })
      logger.info('Successfully fetched call logs', {
        conversationId,
        count: response.data?.calls?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch call logs', error, { conversationId })
      throw error
    }
  }
}

export const aiCallsAPI = {
  list: (page = 1, status = 'all', pageSize = 25) =>
    api.get('/voice/ai-calls', { params: { page, pageSize, status } }),
  detail: (callSid) =>
    api.get(`/voice/ai-calls/${callSid}`),
  recordingUrl: (callSid) =>
    api.get(`/voice/ai-calls/${callSid}/recording-url`)
}

export const timesheetsAPI = {
  async getTimesheets(params) {
    logger.debug('Fetching timesheets', { params: params.toString() })
    try {
      const response = await api.get(`/admin/timesheets?${params.toString()}`)
      logger.info('Successfully fetched timesheets', {
        count: response.data?.data?.length || 0,
        total: response.data?.pagination?.total
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch timesheets', error)
      throw error
    }
  },

  async updateTimesheetStatus(timesheetId, status, reviewedBy = null, adminNotes = null) {
    logger.debug('Updating timesheet status', { timesheetId, status })
    try {
      const payload = { status }
      if (reviewedBy) payload.reviewed_by = reviewedBy
      if (adminNotes) payload.admin_notes = adminNotes

      const response = await api.put(`/admin/timesheets/${timesheetId}/status`, payload)
      logger.info('Successfully updated timesheet status', {
        timesheetId,
        newStatus: status
      })
      return response
    } catch (error) {
      logger.error('Failed to update timesheet status', error, { timesheetId, status })
      throw error
    }
  },

  async getReceiptUrls(s3Keys) {
    logger.debug('Fetching receipt URLs', { count: s3Keys.length })
    try {
      const response = await api.post('/timesheets/receipt-urls', { s3_keys: s3Keys })
      logger.info('Successfully fetched receipt URLs', {
        count: response.data?.urls?.length || 0
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch receipt URLs', error)
      throw error
    }
  }
}

export const recallsAPI = {
  async getRecalls(params) {
    logger.debug('Fetching recalls', { params: params.toString() })
    try {
      const response = await api.get(`/recalls?${params.toString()}`)
      logger.info('Successfully fetched recalls', {
        count: response.data?.data?.length || 0,
        total: response.data?.pagination?.total
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch recalls', error)
      throw error
    }
  },

  async updateRecallStatus(recallId, status) {
    logger.debug('Updating recall status', { recallId, status })
    try {
      const response = await api.put(`/recalls/${recallId}`, { status })
      logger.info('Successfully updated recall status', {
        recallId,
        newStatus: status
      })
      return response
    } catch (error) {
      logger.error('Failed to update recall status', error, { recallId, status })
      throw error
    }
  },

  async sendScheduleLink(recallId) {
    logger.debug('Sending recall schedule link', { recallId })
    try {
      const response = await api.put(`/recalls/${recallId}`, { action: 'send_schedule_link' })
      logger.info('Successfully sent recall schedule link', { recallId })
      return response
    } catch (error) {
      logger.error('Failed to send recall schedule link', error, { recallId })
      throw error
    }
  }
}

export const returningCustomersAPI = {
  async getReport() {
    logger.debug('Fetching returning customers report')
    try {
      const response = await api.get('/reporting/returning-customers', { timeout: 30000 })
      logger.info('Successfully fetched returning customers report')
      return response
    } catch (error) {
      logger.error('Failed to fetch returning customers report', error)
      throw error
    }
  }
}

export const operationsReportAPI = {
  async getMetrics(params = {}) {
    logger.debug('Fetching operations report metrics', { params })
    try {
      const response = await api.get('/reporting/ops-report', {
        params,
        timeout: 30000
      })
      logger.info('Successfully fetched operations report metrics')
      return response
    } catch (error) {
      logger.error('Failed to fetch operations report metrics', error, { params })
      throw error
    }
  }
}

// API methods for inspection reports
export const inspectionAPI = {
  async listAllInspections(params = {}) {
    logger.debug('Fetching all inspections', { params })
    try {
      const response = await api.get('/admin/inspections', { params })
      logger.info('Successfully fetched inspections', {
        count: response.data?.inspections?.length || 0,
        total: response.data?.pagination?.total
      })
      return response
    } catch (error) {
      logger.error('Failed to fetch inspections', error, { params })
      throw error
    }
  },

  async getInspection(inspectionId) {
    logger.debug('Fetching inspection details', { inspectionId })
    try {
      const response = await api.get(`/inspections/${inspectionId}`)
      logger.info('Successfully fetched inspection', { inspectionId })
      return response
    } catch (error) {
      logger.error('Failed to fetch inspection', error, { inspectionId })
      throw error
    }
  },

  async getInspectionReportUrl(inspectionId) {
    logger.debug('Fetching inspection report URL', { inspectionId })
    try {
      const response = await api.get(`/inspections/${inspectionId}/report-url`)
      logger.info('Successfully fetched inspection report URL', { inspectionId })
      return response
    } catch (error) {
      logger.error('Failed to fetch inspection report URL', error, { inspectionId })
      throw error
    }
  },

  async approveInspection(inspectionId, approvedBy) {
    logger.info('Approving inspection', { inspectionId, approvedBy })
    try {
      const response = await api.post(`/admin/inspections/${inspectionId}/approve`, {
        approved_by: approvedBy
      })
      logger.info('Successfully approved inspection', { inspectionId, approvedBy })
      return response
    } catch (error) {
      logger.error('Failed to approve inspection', error, { inspectionId, approvedBy })
      throw error
    }
  },

  async rejectInspection(inspectionId, adminNotes, rejectedBy) {
    logger.info('Rejecting inspection', { inspectionId, rejectedBy })
    try {
      const response = await api.post(`/admin/inspections/${inspectionId}/reject`, {
        admin_notes: adminNotes,
        rejected_by: rejectedBy
      })
      logger.info('Successfully rejected inspection', { inspectionId, rejectedBy })
      return response
    } catch (error) {
      logger.error('Failed to reject inspection', error, { inspectionId, rejectedBy })
      throw error
    }
  },

  async sendInspectionReport(inspectionId, recipientEmail) {
    logger.info('Sending inspection report', { inspectionId, recipientEmail })
    try {
      const response = await api.post(`/admin/inspections/${inspectionId}/send`, {
        recipient_email: recipientEmail
      })
      logger.info('Successfully sent inspection report', { inspectionId, recipientEmail })
      return response
    } catch (error) {
      logger.error('Failed to send inspection report', error, { inspectionId, recipientEmail })
      throw error
    }
  }
}

export default api
